import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Users, 
  GraduationCap, 
  Package, 
  Award,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import Alumni from '../../components/assets/Alumni.jpg';

const benefits = [
  {
    title: 'Job/Career Opportunities',
    description: 'Open doors to exciting careers in international trade and export management',
    icon: Briefcase,
    color: 'blue',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
  },
  {
    title: 'Strong Mentorship Program',
    description: 'Get guidance from industry experts with proven success records',
    icon: Users,
    color: 'green',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
  },
  {
    title: 'Alumni Network',
    description: 'Join a powerful network of successful exporters and trade professionals',
    icon: GraduationCap,
    color: 'purple',
    image: Alumni,
  },
  {
    title: 'Admission Package',
    description: 'Receive LSEM customized materials and comprehensive study resources',
    icon: Package,
    color: 'orange',
    image: 'https://images.unsplash.com/photo-1434626881859-194d67b2b86f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
  }
];

const modules = [
  {
    title: 'Introduction to Export Trade',
    topics: [
      'Global trade fundamentals',
      'Export market dynamics',
      'Trade regulations and compliance',
      'Risk management in international trade'
    ]
  },
  {
    title: 'Identifying Exportable Products and Markets',
    topics: [
      'Market research methodologies',
      'Product selection criteria',
      'Market entry strategies',
      'Competitive analysis'
    ]
  },
  {
    title: 'Export Process and Documentation',
    topics: [
      'Essential export documents',
      'Customs procedures',
      'Trade compliance requirements',
      'Documentation workflow'
    ]
  },
  {
    title: 'Payment Methods and Logistics',
    topics: [
      'International payment systems',
      'Trade finance options',
      'Shipping and logistics',
      'Insurance and risk mitigation'
    ]
  },
  {
    title: 'Practical Tools and Support Systems',
    topics: [
      'Export management software',
      'Trade databases and resources',
      'Government support programs',
      'Industry networks'
    ]
  }
];

const Zero2ExportBCEM = () => {
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);

  const handleEnrollClick = () => {
    const packageSection = document.getElementById('zero2export-packages');
    if (packageSection) {
      packageSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-4"
          >
            Zero2Export&trade; Advantage
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4"
          >
            Basic Certificate in Export Management
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Kill 2 birds with 1 stone - transform your career with our internationally recognized certification program
          </motion.p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 to-gray-900/60 rounded-2xl transition-opacity group-hover:opacity-75" />
              <img
                src={benefit.image}
                alt={benefit.title}
                className="w-full h-64 object-cover rounded-2xl"
              />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className={`w-12 h-12 bg-${benefit.color}-100 rounded-lg flex items-center justify-center mb-4`}>
                  <benefit.icon className={`h-6 w-6 text-${benefit.color}-600`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-200">{benefit.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Curriculum Modules */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">A Deep Dive into BCEM</h3>
          <div className="grid gap-8">
            {modules.map((module, index) => (
              <motion.div
                key={module.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6"
              >
                <h4 className="text-lg font-semibold text-gray-900 mb-4">{module.title}</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {module.topics.map((topic, topicIndex) => (
                    <div key={topicIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                      <span className="text-gray-600">{topic}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Certificate Feature */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center"
        >
          <Award className="h-16 w-16 text-white mx-auto mb-6" />
          <h3 className="text-2xl font-bold text-white mb-4">
            Earn Your Professional Certificate with Zero2Export&trade;
          </h3>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Get your framed certificate in a 3 days fast-track and join the ranks of successful export professionals. Our certificates are recognized by leading trade organizations worldwide.
          </p>
          <button 
            onClick={handleEnrollClick}
            className="inline-flex items-center px-6 py-3 border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
          >
            Begin Your Journey
            <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Zero2ExportBCEM;